// ** Amplify
import { API } from "aws-amplify"
// **  store names
import { STORES } from "@constants/redux"

// ** main app Logger
import { appLogger } from "@helpers/logger"

// ** constants
import { globalConst } from "@constants/global"

// ** Customer Souq API Service
import customerSouqApiService from "../../services/customerSouqApiService"
import { handleApiAuthError } from "@helpers/authSession"

// **  utility
import { isEmpty, drop } from "lodash"

// ** fuzzy search support
import Fuse from "fuse.js"
import { devLog } from "@helpers/devLog"

const shouldSkipProtectedDashboardCalls = () => {
  // Server-authoritative admin dashboard: disable local fallback mode.
  return false
}

const LOCAL_SOUQ_STORAGE_KEY = "admin_local_souq_items_v1"
const LOCAL_SOUQ_BANNERS_STORAGE_KEY = "admin_local_souq_banners_v1"
const LOCAL_SOUQ_MESSAGES_STORAGE_KEY = "admin_local_souq_messages_v1"

const readLocalSouqItems = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(LOCAL_SOUQ_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (_error) {
    return []
  }
}

const normalizeLocalSouqImages = (item) => {
  if (!item || typeof item !== "object") return item
  const images = Array.isArray(item.images) ? item.images : []
  const cover = typeof item.cover === "string" ? item.cover : ""
  const coverIsData = cover.startsWith("data:image/")
  const nextImages = images.map((img) => {
    if (typeof img === "string" && img.startsWith("blob:") && coverIsData) {
      return cover
    }
    return img
  })
  const nextCover =
    typeof cover === "string" && cover.startsWith("blob:") && nextImages.some((img) => typeof img === "string" && img.startsWith("data:image/"))
      ? nextImages.find((img) => typeof img === "string" && img.startsWith("data:image/"))
      : cover
  return {
    ...item,
    images: nextImages,
    cover: nextCover
  }
}

const writeLocalSouqItems = (items) => {
  if (typeof window === "undefined") return
  window.localStorage.setItem(
    LOCAL_SOUQ_STORAGE_KEY,
    JSON.stringify(Array.isArray(items) ? items : [])
  )
}

const upsertLocalSouqItem = (pk, updater) => {
  const current = readLocalSouqItems()
  const next = current.map((item) => (item.pk === pk ? updater(item) : item))
  writeLocalSouqItems(next)
  return next
}

const readLocalSouqBanners = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(LOCAL_SOUQ_BANNERS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (_error) {
    return []
  }
}

const writeLocalSouqBanners = (items) => {
  if (typeof window === "undefined") return
  window.localStorage.setItem(
    LOCAL_SOUQ_BANNERS_STORAGE_KEY,
    JSON.stringify(Array.isArray(items) ? items : [])
  )
}

const readLocalSouqMessages = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(LOCAL_SOUQ_MESSAGES_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (_error) {
    return []
  }
}

const writeLocalSouqMessages = (items) => {
  if (typeof window === "undefined") return
  window.localStorage.setItem(
    LOCAL_SOUQ_MESSAGES_STORAGE_KEY,
    JSON.stringify(Array.isArray(items) ? items : [])
  )
}

/** initla state for auth store  */
const initialState = {
  data: [],
  messages: [],
  banners: [],
  count: "",
  pagination: { page: 1, limit: 20, total: 0 },
  current: {},
  perPageValues: [
    { value: 10, label: 10 },
    { value: 25, label: 25 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
    { value: 150, label: 150 },
    { value: 200, label: 200 }
  ],
  itemPerPage: 100,
  bannerPerPage: 100,
  currentPage: 1,
  currentBannerPage: 1,
  filters: {
    status: {
      value: "ALL",
      label: "label_all"
    },

  },
  filtersMessages: {
    type: {
      value: "ALL",
      label: "label_all"
    },
  },  
  filtersItems: {
    parent: null,
    type: {
      value: "ALL",
      label: "label_all"
    },
    status: {
      value: "ALL",
      label: "label_all"
    }
  },
  message: "",
  keyword: null,
  action: globalConst.action.NONE
}

export const souq = {
  state: initialState,

  reducers: {
    /**
     * Set translate message
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setMessages: (s, payload) => {
      return {
        ...s,
        messages: payload
      }
    },
    setCurrentPage: (s, payload) => {
      return {
        ...s,
        currentPage: payload
      }
    },
    setCurrentBannerPage: (s, payload) => {
      return {
        ...s,
        currentBannerPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCount: (s, payload) => {
      return {
        ...s,
        count: payload
      }
    },
    setPagination: (s, payload) => {
      return {
        ...s,
        pagination: { ...s.pagination, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setData: (s, payload) => {
      return {
        ...s,
        data: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setBanners: (s, payload) => {
      return {
        ...s,
        banners: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateItemRecord: (s, payload) => {
      const index = s.data.findIndex((e) => e.pk === payload.pk)
      const item = s.data[index]
      item.images = item.images.filter((e) => e !== payload.image)
      const _data = s.data.filter((e) => e.pk !== payload.pk)
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateSingleItem: (s, payload) => {
      const index = s.data.findIndex((e) => e.pk === payload.pk)
      const item = payload
      const _data = s.data.filter((e) => e.pk !== payload.pk)
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setItems: (s, payload) => {
      return {
        ...s,
        items: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setMessages: (s, payload) => {
      return {
        ...s,
        messages: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    pushNewData: (s, payload) => {
      if (!payload || !payload.pk) {
        return { ...s, data: [...s.data, payload].filter(Boolean) }
      }
      const rest = s.data.filter((e) => e.pk !== payload.pk)
      return {
        ...s,
        data: [...rest, payload]
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setFilters: (s, payload) => {
      return {
        ...s,
        filters: { ...s.filters, ...payload }
      }
    },
    setFiltersItems: (s, payload) => {
      return {
        ...s,
        filtersItems: { ...s.filtersItems, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    replaceRecord: (s, payload) => {
      const _data = s.data
      const index = s.data.findIndex((e) => e.pk === payload.pk)
      const item = s.data.find((e) => e.pk === payload.pk)
      item.status = payload.status
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setKeyword: (s, payload) => {
      return {
        ...s,
        keyword: payload
      }
    },
    setFiltersMessages: (s, payload) => {
      return {
        ...s,
        filtersMessages: { ...s.filtersMessages, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setitemPerPage: (s, payload) => {
      return {
        ...s,
        itemPerPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setBannersPerPage: (s, payload) => {
      return {
        ...s,
        bannerPerPage: payload
      }
    },
    // bannerPerPage
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrent: (s, payload) => {
      return {
        ...s,
        current: payload
      }
    },

    setMessage: (s, payload) => {
      return {
        ...s,
        message: payload
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setAction: (s, payload) => {
      return {
        ...s,
        action: payload
      }
    }
  },
  selectors: (slice, createSelector) => ({
    /**
     *
     * @returns
     */
    error() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.exhibitions],
        (exhibitions, exhibitionsLoading) => {
          return exhibitionsLoading.error || ""
        }
      )
    },

    /**
     * success selector used in different
     * auth actions
     *
     * @returns {String}
     */
    success() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.souq],
        (souq, souqLoading) => souqLoading.success
      )
    },
    /**
     *
     * @returns
     */
    paginationData() {
      return createSelector([slice], (souq) => {
        let _data = souq.data

        if (!!souq.keyword) {
          const options = {
            keys: ["title", "titleAr", "descr", "descrAr","type"]
          }
          const fuse = new Fuse(souq.data, options)
          _data = fuse.search(souq.keyword)
          _data = _data.map((e) => e.item)
        }
        _data = 
        souq.filtersItems.type.value !== "ALL" ?_data.filter((e) => e.type === souq.filtersItems.type.value):_data
        const offset = (souq.currentPage - 1) * souq.itemPerPage
        _data = drop(_data, offset).slice(0, souq.itemPerPage)
        return _data
      })
    },
    /**
     *
     * @returns
     */
    paginationBannersData() {
      return createSelector([slice], (souq) => {
        let _data = souq.banners
        if (!!souq.keyword) {
          const options = {
            keys: ["phone", "stats"]
          }
          const fuse = new Fuse(souq.items, options)
          _data = fuse.search(souq.keyword)
          _data = _data.map((e) => e.item)
  
        }
        const offset = (souq.currentPage - 1) * souq.bannerPerPage
          _data = drop(_data, offset).slice(0, souq.bannerPerPage)
        return _data
      })
    },
    /**
     *
     * @returns
     */
    paginationMessagesData() {
      return createSelector([slice], (souq) => {
        let _data = souq.messages

        // if (!!souq.keyword) {
        //   const options = {
        //     keys: ["message"]
        //   }
        //   const fuse = new Fuse(souq.messages, options)
        //   _data = fuse.search(souq.keyword)
        //   _data = _data.map((e) => e.item)
        //   const offset = (souq.currentPage - 1) * souq.itemPerPage
        //   _data = drop(_data, offset).slice(0, souq.itemPerPage)
        // }
        _data = souq.filtersMessages.type.value !== "ALL" ?_data.filter((e) => e.type === souq.filtersMessages.type.value):_data
        const offset = (souq.currentPage - 1) * souq.itemPerPage
        _data = drop(_data, offset).slice(0, souq.itemPerPage)
        return _data
      })
    }
  }),
  effects: (dispatch) => ({
    /**
     *
     */
    async fetchSouq() {
      appLogger.info("fetchSouq", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.FETCH_SOUQ)
      if (shouldSkipProtectedDashboardCalls()) {
        const localItems = readLocalSouqItems().map(normalizeLocalSouqImages)
        writeLocalSouqItems(localItems)
        this.setData(localItems)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const items=await API.get(
          "adminsApi",
          `/souq/v1/`,
          {}
        )
        

        // Filter out customer souq items to prevent them from showing in regular souq
        const filteredItems = items.filter(item => {
          // Exclude items that have customer-specific fields or source
          const hasCustomerFields = item.customerName || item.customerPhone || item.customerEmail
          const isCustomerSource = item.source === 'CUSTOMER' || item.source?.includes('customer')
          
          // Exclude items with APPROVED status that don't have a title (customer souq items)
          const isApprovedCustomerItem = item.status === 'APPROVED' && !item.title
          
          // Exclude items that are missing essential souq fields (title, description, etc.)
          const isIncompleteItem = !item.title || !item.descr
          
          const shouldExclude = hasCustomerFields || isCustomerSource || isApprovedCustomerItem || isIncompleteItem
          
          devLog('Filtering item:', {
            pk: item.pk,
            title: item.title,
            source: item.source,
            customerName: item.customerName,
            status: item.status,
            keys: Object.keys(item),
            hasCustomerFields: hasCustomerFields,
            isCustomerSource: isCustomerSource,
            isApprovedCustomerItem: isApprovedCustomerItem,
            isIncompleteItem: isIncompleteItem,
            shouldExclude: shouldExclude,
            willShow: !shouldExclude
          })
          
          return !shouldExclude
        })
        

        this.setData(filteredItems)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setData([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    /**
     *
     */
    async fetchBanners() {
      appLogger.info("fetchBanners", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.FETCH_BANNERS)
      if (shouldSkipProtectedDashboardCalls()) {
        this.setBanners(readLocalSouqBanners())
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const { items, count } = await API.get("souqApiGateway", "/banners")
        this.setBanners(items)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setBanners([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    /**
     *
     */
    async fetchMessages() {
      appLogger.info("fetchMessages", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.FETCH_MESSAGES)
      if (shouldSkipProtectedDashboardCalls()) {
        this.setMessages(readLocalSouqMessages())
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const result=await API.get(
          "adminsApi",
          `/req-msgs/v1`,
          {
          }
        )
        this.setMessages(result)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setMessages([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    // FETCH_MESSAGES

    /**
     *
     */
    async saveItem(payload) {
      appLogger.info("saveItem", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.SAVE_SOUQ_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const localItem = {
          ...payload,
          pk: payload.pk || `local-souq-${Date.now()}`,
          sk: payload.sk || now,
          status: payload.status || "PENDING",
          createdAt: payload.createdAt || now,
          updatedAt: now
        }
        const next = [...readLocalSouqItems(), localItem]
        writeLocalSouqItems(next)
        this.pushNewData(localItem)
        this.setMessage("Item saved susccesfully")
        this.setAction(globalConst.action.NONE)
        return localItem
      }
      try {

        const items = await API.post(
          "adminsApi",
          `/souq/v1/`,
          {
            body: payload
          }
        )

        if (items && typeof items === "object" && items.pk) {
          this.pushNewData(items)
        }

        this.setMessage("Item saved susccesfully")
        this.setAction(globalConst.action.NONE)
        return items
      } catch (error) {
        this.setMessage("Failed to save item")
        this.setAction(globalConst.action.NONE)
        return null
      }
    },

    /**
     *
     * @param {*} payload
     */
    async saveBanner(payload) {
      appLogger.info("saveBanner", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.SAVE_SOUQ_BANNER)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const banner = {
          ...payload,
          pk: payload.pk || `local-banner-${Date.now()}`,
          createdAt: payload.createdAt || now,
          updatedAt: now
        }
        const next = [...readLocalSouqBanners(), banner]
        writeLocalSouqBanners(next)
        this.setBanners(next)
        this.setMessage("msg_banner_saved_successfully")
        this.setAction(globalConst.action.NONE)
        return banner
      }
      try {
        const result = await API.post("souqApiGateway", "/banners", {
          body: payload
        })

        this.setMessage("msg_banner_saved_successfully")
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} payload
     */
    async sendNotifications(payload) {
      appLogger.info("saveBanner", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.SEND_NOTIFICATION)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const message = {
          ...payload,
          pk: payload.pk || `local-message-${Date.now()}`,
          createdAt: payload.createdAt || now,
          updatedAt: now
        }
        const next = [...readLocalSouqMessages(), message]
        writeLocalSouqMessages(next)
        this.setMessages(next)
        this.setMessage("msg_notf_sent_successfully")
        this.setAction(globalConst.action.NONE)
        return message
      }
      try {
        const result = await API.post("souqApiGateway", "/notify", {
          body: payload
        })

        this.setMessage("msg_notf_sent_successfully")
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     * @param {*} pk
     */
    async deleteBanner(pk) {
      appLogger.info("deleteBanner", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.DELETE_SOUQ_BANNER)
      if (shouldSkipProtectedDashboardCalls()) {
        const next = readLocalSouqBanners().filter((item) => item.pk !== pk)
        writeLocalSouqBanners(next)
        this.setBanners(next)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const result = await API.del("souqApiGateway", `/banners/${pk}`, {
          body: payload
        })
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     * @param {*} payload
     */
    async updateSouqItem(payload) {
      appLogger.info("updateSouqItem", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.UPDATE_SOUQ_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalSouqItem(payload.pk, (item) => ({
          ...item,
          ...payload,
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("Item updated successfully")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
        const items=await API.put(
          "adminsApi",
          `/souq/v1/${payload.pk}`,{
            body:{...payload}
          }
        )
        if (items && typeof items === "object") {
          this.pushNewData(items)
          if (items.pk === payload.pk) {
            this.setCurrent(items)
          }
        }
        this.setMessage("Item updated successfully")
        return items
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     * @param {*} pk
     */
    async getItem(pk) {
      appLogger.info("getItem", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.GET_SOUQ_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        const item = readLocalSouqItems().find((e) => e.pk === pk) || {}
        this.setCurrent(item)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const item=await API.get(
          "adminsApi",
          `/souq/v1/${pk}`
        )

        this.setCurrent(item)
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     * @param {*} pk
     */
    async getBanner(pk) {
      appLogger.info("getItem", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.GET_BANNER)
      try {
        const { item } = await API.get("souqApiGateway", `/banners/${pk}`)
        this.setCurrent(item)
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} pk
     */
    async deleteSouq(payload) {
      appLogger.info("deleteSouq", `[REDUX][${STORES.EXHIBITIONS}]`)
      this.setAction(globalConst.action.DELETE_SOUQ_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        const next = readLocalSouqItems().filter((item) => item.pk !== payload.pk)
        writeLocalSouqItems(next)
        this.setData(next)
        this.setMessage("Item deleted successfully")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const items=await API.post(
          "adminsApi",
          `/souq/v1/${payload.pk}/delete`,{
            body:{sk:payload.sk}
          }
        )
        this.setMessage("Item deleted successfully")
      } catch (error) {
        throw new Error(error)
      }
    },
    async activate(payload) {
      appLogger.info("activate", `[REDUX][${STORES.EXHIBITIONS}]`)
      this.setAction(globalConst.action.ACTIVATE_SOUQ_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalSouqItem(payload.pk, (item) => ({
          ...item,
          status: "ACTIVE",
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("Item activated successfully")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const items=await API.post(
          "adminsApi",
          `/souq/v1/${payload.pk}/activate`,{
            body:{sk:payload.sk}
          }
        )
        this.setMessage("Item activated successfully")
      } catch (error) {
        throw new Error(error)
      }
    },
    // ACTIVATE_SOUQ_ITEM

    /**
     *
     * @param {*} param0
     */
    async deleteImage({ pk, image, sk, images }) {
      appLogger.info("updateCover", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.DELETE_SOUQ_ITEM_IMAGE)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalSouqItem(pk, (item) => ({
          ...item,
          images: Array.isArray(images)
            ? images
            : (item.images || []).filter((img) => img !== image),
          updatedAt: now
        }))
        this.setData(next)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        await API.put(
          "adminsApi",
          `/souq/v1/${pk}`,{
            body:{images,sk}
          }
        )
     
        this.updateItemRecord({ pk, image })
      } catch (error) {
        throw new Error(error)
      }
    },

    async addImage({ pk, image,sk, images }) {
      appLogger.info("addImage", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.SAVE_SOUQ_ITEM_IMAGE)

      const _images = images
      _images.push(image)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalSouqItem(pk, (item) => ({
          ...item,
          images: _images,
          updatedAt: now
        }))
        this.setData(next)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
      
        await API.put(
          "adminsApi",
          `/souq/v1/${pk}`,{
            body:{
              images:_images,
              sk
            
            }
          }
        )

      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} pk
     */
    async refreshItem(pk) {
      appLogger.info("refreshItem", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.GET_SOUQ_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        const result = readLocalSouqItems().find((item) => item.pk === pk)
        if (result) {
          this.updateSingleItem(result)
        }
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
     
        const result=await API.get(
          "adminsApi",
          `/souq/v1/${pk}`
        )

        this.updateSingleItem(result)
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     */
    onAcionReset() {
      this.setAction(globalConst.action.NONE)
    },
    /**
     *
     * @param {*} param0
     */
    resetCurrentRecord(payload = {}) {
      this.setCurrent(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onItemPerPageChange(payload) {
      this.setitemPerPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onBannersPerPageChange(payload) {
      this.setBannersPerPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onFiltersChange(payload) {
 
      this.setFilters(payload)
    },
    onFiltersItemsChange(payload){
      this.setFiltersItems(payload)
    },
    onFiltersMessagesChange(payload){
      this.setFiltersMessages(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onCurrentPageChange(payload) {
      this.setCurrentPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onKeywordChange(payload) {
      this.setKeyword(payload)
    },

    // Customer Souq Effects
    /**
     * Fetch customer new souq items
     */
    async fetchCustomerNewSouqItems(params = {}) {
      appLogger.info("fetchCustomerNewSouqItems", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.FETCH_CUSTOMER_NEW_SOUQ_ITEMS)

      try {
        const response = await customerSouqApiService.getNewItems(params)
        
        // Normalize response shapes: array | {items} | {data} | {data: {items}}
        let items = []
        if (Array.isArray(response)) {
          items = response
        } else if (response && Array.isArray(response.items)) {
          items = response.items
        } else if (response && Array.isArray(response.data)) {
          items = response.data
        } else if (response && response.data && Array.isArray(response.data.items)) {
          items = response.data.items
        } else {
          items = []
        }
        
        
        // Backend already returns ACTIVE + published=true for approved items
        this.setData(items)
        // pagination
        const pagination = response.pagination || response.data?.pagination
        if (pagination) this.setPagination(pagination)
        this.setMessage("Customer new souq items fetched successfully")
      } catch (error) {
        appLogger.error("fetchCustomerNewSouqItems", error)
        this.setMessage("Failed to fetch customer new souq items")
        this.setData([])
      }
    },

    /**
     * Fetch customer pending souq items
     */
    async fetchCustomerPendingSouqItems(params = {}) {
      appLogger.info("fetchCustomerPendingSouqItems", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.FETCH_CUSTOMER_PENDING_SOUQ_ITEMS)

      try {
        const response = await customerSouqApiService.getPendingItems(params)
        
        // Normalize response shapes: array | {items} | {data} | {data: {items}}
        let items = []
        if (Array.isArray(response)) {
          items = response
        } else if (response && Array.isArray(response.items)) {
          items = response.items
        } else if (response && Array.isArray(response.data)) {
          items = response.data
        } else if (response && response.data && Array.isArray(response.data.items)) {
          items = response.data.items
        } else {
          items = []
        }
        
        
        // Normalize status: customer-submitted items with NEW -> PENDING for UI consistency
        const normalizedItems = items.map(item => {
          if (item?.source === 'CUSTOMER' && item?.status === 'NEW') {
            return { ...item, status: 'PENDING' }
          }
          return item
        })
        
        this.setData(normalizedItems)
        // pagination
        const pagination = response.pagination || response.data?.pagination
        if (pagination) this.setPagination(pagination)
        this.setMessage("Customer pending souq items fetched successfully")
      } catch (error) {
        appLogger.error("fetchCustomerPendingSouqItems", error)
        this.setMessage("Failed to fetch customer pending souq items")
        this.setData([])
      }
    },

    /**
     * Change customer souq item status
     */
    async changeCustomerSouqItemStatus(payload) {
      appLogger.info("changeCustomerSouqItemStatus", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.CHANGE_CUSTOMER_SOUQ_ITEM_STATUS)

      try {
        let result
        if (payload.status === 'APPROVED' || payload.status === 'ACTIVE') {
          // Map approve to backend approve endpoint, which sets ACTIVE + published=true
          const item = (this.state?.data || []).find(i => i.pk === payload.pk)
          const sk = item?.sk
          result = await customerSouqApiService.approveItem(payload.pk, {
            adminNotes: payload.reason || '',
            approvedBy: 'admin',
            ...(sk ? { sk } : {})
          })
        } else if (payload.status === 'REJECTED') {
          result = await customerSouqApiService.rejectItem(payload.pk, {
            rejectionReason: payload.reason || '',
            rejectedBy: 'admin'
          })
        } else {
          result = await customerSouqApiService.changeItemStatus(payload.pk, payload.status, payload.reason)
        }
        
        // Update local state - remove item from current list if status changed
        const currentDataSource = Array.isArray(this.state?.data)
          ? this.state.data
          : (Array.isArray(this.data) ? this.data : [])

        // If we somehow don't have the list locally, avoid clearing the UI; refresh from server instead
        if (!Array.isArray(currentDataSource) || currentDataSource.length === 0) {
          try {
            // Fetch NEW first, then PENDING so PENDING is the final list in store
            await this.fetchCustomerNewSouqItems({ page: 1, limit: this.pagination?.limit || 10 })
            await this.fetchCustomerPendingSouqItems({ page: 1, limit: this.pagination?.limit || 10 })
          } catch (_) {}
          this.setMessage("Customer souq item status updated successfully")
          return { success: true }
        }

        const currentData = currentDataSource
        let updatedData
        
            if (payload.status === 'APPROVED' || payload.status === 'ACTIVE') {
              // For APPROVED status, remove the item from pending list (it should move to new items)
              updatedData = currentData.filter(item => item.pk !== payload.pk)
            } else if (payload.status === 'PUBLISHED' || payload.status === 'REJECTED') {
          // Remove the item from current list (it will appear in the other list)
          updatedData = currentData.filter(item => item.pk !== payload.pk)
        } else {
          // Update the item in place for other status changes
          updatedData = currentData.map(item => {
            if (item.pk === payload.pk) {
              const updatedItem = {
                ...item,
                status: payload.status,
                updatedAt: new Date().toISOString()
              }
              
              // Add status-specific fields
              if (payload.status === 'APPROVED' || payload.status === 'ACTIVE') {
                updatedItem.approvedAt = new Date().toISOString()
                updatedItem.approvedBy = 'admin'
              } else if (payload.status === 'REJECTED') {
                updatedItem.rejectionReason = payload.reason
                updatedItem.rejectedAt = new Date().toISOString()
                updatedItem.rejectedBy = 'admin'
              }
              
              return updatedItem
            }
            return item
          })
            }
            
            this.setData(updatedData)
        
        // Refresh lists from server to reflect persisted changes
        try {
          // Fetch NEW first, then PENDING so PENDING remains the final state in store
          await this.fetchCustomerNewSouqItems({ page: 1, limit: this.pagination?.limit || 10 })
          await this.fetchCustomerPendingSouqItems({ page: 1, limit: this.pagination?.limit || 10 })
          await this.countCustomerPendingSouqItems()
          await this.countCustomerNewSouqItems()
        } catch (e) {
          // ignore refresh errors; UI already updated locally
        }
        
            // If item was approved, simulate adding it to new items list
            if (payload.status === 'APPROVED') {
              const approvedItem = currentData.find(item => item.pk === payload.pk)
              if (approvedItem) {
                const updatedApprovedItem = {
                  ...approvedItem,
                  status: 'APPROVED',
                  approvedAt: new Date().toISOString(),
                  approvedBy: 'admin',
                  updatedAt: new Date().toISOString()
                }
                // Note: This is a simulation - in reality, the backend would handle this
                // For now, the item will appear to be removed from pending and should appear in new items
                // when the backend is fixed and the new items API returns the correct data
              }
            }

        // Also update the current record if it's the same item
        if (this.current && this.current.pk === payload.pk) {
          if (payload.status === 'APPROVED' || payload.status === 'ACTIVE') {
            const updatedCurrent = {
              ...this.current,
              status: 'ACTIVE',
              approvedAt: new Date().toISOString(),
              approvedBy: 'admin',
              updatedAt: new Date().toISOString()
            }
            this.setCurrent(updatedCurrent)
          } else if (payload.status === 'PUBLISHED' || payload.status === 'REJECTED') {
            this.setCurrent(null)
          }
        }
        
        this.setMessage("Customer souq item status updated successfully")
        return { success: true }
      } catch (error) {
        appLogger.error("changeCustomerSouqItemStatus", error)
        this.setMessage("Failed to update customer souq item status")
        throw error
      }
    },

    /**
     * Publish customer souq item
     */
    async publishCustomerSouqItem(pk) {
      appLogger.info("publishCustomerSouqItem", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.PUBLISH_CUSTOMER_SOUQ_ITEM)

      try {
        await customerSouqApiService.publishItem(pk)
        
        // Update local state
        const currentData = this.state.data
        const updatedData = currentData.map(item => {
          if (item.pk === pk) {
            return {
              ...item,
              status: "APPROVED",
              published: true,
              publishedAt: new Date().toISOString()
            }
          }
          return item
        })
        
        this.setData(updatedData)
        this.setMessage("Customer souq item published successfully")
        return { success: true }
      } catch (error) {
        appLogger.error("publishCustomerSouqItem", error)
        this.setMessage("Failed to publish customer souq item")
        throw error
      }
    },

    /**
     * Count customer new souq items
     */
    async countCustomerNewSouqItems() {
      appLogger.info("countCustomerNewSouqItems", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.COUNT_CUSTOMER_NEW_SOUQ_ITEMS)

      try {
        const result = await customerSouqApiService.getNewItemsCount()
        const count = result.count || 0
        this.setMessage("Customer new souq items count updated")
        return count
      } catch (error) {
        if (handleApiAuthError(error)) return 0
        appLogger.error("countCustomerNewSouqItems", error)
        this.setMessage("Failed to count customer new souq items")
        return 0
      }
    },

    /**
     * Count customer pending souq items
     */
    async countCustomerPendingSouqItems() {
      appLogger.info("countCustomerPendingSouqItems", `[REDUX][${STORES.SOUQ}]`)
      this.setAction(globalConst.action.COUNT_CUSTOMER_PENDING_SOUQ_ITEMS)

      try {
        const result = await customerSouqApiService.getPendingItemsCount()
        const count = result.count || 0
        this.setMessage("Customer pending souq items count updated")
        return count
      } catch (error) {
        if (handleApiAuthError(error)) return 0
        appLogger.error("countCustomerPendingSouqItems", error)
        this.setMessage("Failed to count customer pending souq items")
        return 0
      }
    }
  })
}
